import { useEffect, useRef, useState } from 'react';
import { Scorm12API, Scorm2004API, type Settings } from 'scorm-again';

import type { LessonProgressSequenceActivityAttempt } from '@didaskill/domain/lesson-progresses/types';

const getScoreOnPercent = (rawScore: number, minScore: number, maxScore: number) => {
  return Math.round(((rawScore - minScore) / (maxScore - minScore)) * 100);
};

declare global {
  interface Window {
    API?: any;
    API_1484_11?: any;
  }
}

export type ScormApiParams = {
  version: '2004' | '1.2';
  learnerId: string;
  learnerName: string;
  onStartActivity?: (data: {
    suspendData?: string;
  }) => Promise<LessonProgressSequenceActivityAttempt>;
  onDataChange?: (data: { attemptId: string; suspendData?: string }) => Promise<void>;
  onCompleteActivity?: (data: {
    attemptId: string;
    suspendData: string;
    score: number;
  }) => Promise<void>;
  initialSuspendData?: string;
  initialAttemptId?: string;
};

export const useScormApi = ({
  version,
  learnerId,
  learnerName,
  onStartActivity = Promise.resolve,
  onDataChange = Promise.resolve,
  onCompleteActivity = Promise.resolve,
  initialSuspendData,
  initialAttemptId,
}: ScormApiParams) => {
  const [isApiReady, setIsApiReady] = useState(false);

  // Avoid double-calling start callbacks (React StrictMode, re-renders, etc.)
  const attemptIdRef = useRef<string | undefined>(initialAttemptId);
  const startedRef = useRef(false);
  const isResumingRef = useRef(!!initialSuspendData);
  const terminatedRef = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <I presume this is correct, but I don't know enough about the scorm-again library to be sure>
  useEffect(() => {
    const settings: Settings = {};

    if (version === '2004') {
      window.API_1484_11 = new Scorm2004API(settings);
    } else {
      window.API = new Scorm12API(settings);
    }

    try {
      if (version === '2004') {
        window.API_1484_11.cmi.learner_id = learnerId;
        window.API_1484_11.cmi.learner_name = learnerName;

        if (initialSuspendData) {
          window.API_1484_11.cmi.suspend_data = initialSuspendData;
        }

        window.API_1484_11.on('Initialize', async () => {
          if (!startedRef.current) {
            startedRef.current = true;

            if (!isResumingRef.current && !attemptIdRef.current) {
              const res = await onStartActivity({ suspendData: undefined });
              attemptIdRef.current = res.id;
            }
          }
        });

        window.API_1484_11.on('SetValue.cmi.suspend_data', (_el: string, value: string) => {
          if (startedRef.current && !terminatedRef.current && attemptIdRef.current) {
            onDataChange({ attemptId: attemptIdRef.current!, suspendData: value });
          }
        });

        window.API_1484_11.on(
          'SetValue.cmi.completion_status',
          async (_el: string, value: 'not attempted' | 'incomplete' | 'completed') => {
            if (!terminatedRef.current && value === 'completed') {
              terminatedRef.current = true;

              const rawScore = parseInt(window.API_1484_11.cmi.score.raw, 10) || 0;
              const maxScore = parseInt(window.API_1484_11.cmi.score.max, 10) || 100;
              const minScore = parseInt(window.API_1484_11.cmi.score.min, 10) || 0;

              const scoreOnPercent = getScoreOnPercent(rawScore, minScore, maxScore);

              await onCompleteActivity({
                attemptId: attemptIdRef.current!,
                suspendData: window.API_1484_11.cmi.suspend_data
                  ? window.API_1484_11.cmi.suspend_data
                  : undefined,
                score: scoreOnPercent,
              });
            }
          },
        );

        window.API_1484_11.on('Terminate', async () => {
          if (!terminatedRef.current) {
            terminatedRef.current = true;

            const completionStatus = window.API_1484_11.cmi.completion_status;

            if (completionStatus === 'completed') {
              const rawScore = parseInt(window.API_1484_11.cmi.score.raw, 10) || 0;
              const maxScore = parseInt(window.API_1484_11.cmi.score.max, 10) || 100;
              const minScore = parseInt(window.API_1484_11.cmi.score.min, 10) || 0;

              const scoreOnPercent = getScoreOnPercent(rawScore, minScore, maxScore);

              await onCompleteActivity({
                attemptId: attemptIdRef.current!,
                suspendData: window.API_1484_11.cmi.suspend_data,
                score: scoreOnPercent,
              });
            } else {
              // Not completed, just save progress
              await onDataChange({
                attemptId: attemptIdRef.current!,
                suspendData: window.API_1484_11.cmi.suspend_data,
              });
            }
          }
        });
      } else {
        window.API.cmi.core.student_id = learnerId;
        window.API.cmi.core.student_name = learnerName;

        // TODO: not sure if this is the right way to set suspend data in 1.2
        if (initialSuspendData) {
          window.API.cmi.core.suspend_data = initialSuspendData;
        }

        window.API.on('LMSInitialize', async () => {
          if (!startedRef.current) {
            startedRef.current = true;

            if (!isResumingRef.current && !attemptIdRef.current) {
              const res = await onStartActivity({ suspendData: undefined });
              attemptIdRef.current = res.id;
            }
          }
        });

        window.API.on('LMSSetValue.cmi.suspend_data', (_el: string, value: string) => {
          if (startedRef.current && !terminatedRef.current && attemptIdRef.current) {
            onDataChange({ attemptId: attemptIdRef.current!, suspendData: value });
          }
        });

        window.API.on(
          'LMSSetValue.cmi.core.lesson_status',
          async (_el: string, value: 'not attempted' | 'incomplete' | 'completed') => {
            if (!terminatedRef.current && value === 'completed') {
              terminatedRef.current = true;

              const rawScore = parseInt(window.API.cmi.core.score.raw, 10) || 0;
              const maxScore = parseInt(window.API.cmi.core.score.max, 10) || 100;
              const minScore = parseInt(window.API.cmi.core.score.min, 10) || 0;

              const scoreOnPercent = getScoreOnPercent(rawScore, minScore, maxScore);

              await onCompleteActivity({
                attemptId: attemptIdRef.current!,
                suspendData: window.API.cmi.core.suspend_data
                  ? window.API.cmi.core.suspend_data
                  : undefined,
                score: scoreOnPercent,
              });
            }
          },
        );

        window.API.on('LMSFinish', async () => {
          if (!terminatedRef.current) {
            terminatedRef.current = true;

            const lessonStatus = window.API.cmi.core.lesson_status;

            if (lessonStatus === 'completed') {
              const rawScore = parseInt(window.API.cmi.core.score.raw, 10) || 0;
              const maxScore = parseInt(window.API.cmi.core.score.max, 10) || 100;
              const minScore = parseInt(window.API.cmi.core.score.min, 10) || 0;

              const scoreOnPercent = getScoreOnPercent(rawScore, minScore, maxScore);

              await onCompleteActivity({
                attemptId: attemptIdRef.current!,
                suspendData: window.API.cmi.core.suspend_data,
                score: scoreOnPercent,
              });
            } else {
              // Not completed, just save progress
              await onDataChange({
                attemptId: attemptIdRef.current!,
                suspendData: window.API.cmi.core.suspend_data,
              });
            }
          }
        });
      }
    } catch (_error) {}

    setIsApiReady(true);

    return () => {
      if (version === '2004') {
        delete window.API_1484_11;
      } else {
        delete window.API;
      }

      setIsApiReady(false);

      attemptIdRef.current = undefined;
      startedRef.current = false;
      terminatedRef.current = false;
      isResumingRef.current = Boolean(initialSuspendData);
    };
  }, [version, learnerId, learnerName]);

  return { isApiReady };
};
